import React, { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import {
  Drawer, Button, Select, Input, Avatar, Spin, Tooltip, message, Modal, Tabs, Empty,
} from "antd";
import {
  PlusOutlined, DeleteOutlined, SendOutlined, InboxOutlined, WechatOutlined, UploadOutlined,
} from "@ant-design/icons";
import { API_DOMAIN } from "../constants";
import { AuthContext } from "../contexts/AuthContext";
import { URGENCY_CFG } from "./pipeline";
import { getAgentColor, agentInitials, _fetchSRMUsers } from "./helpers";
import { _resolveMediaUrl } from "./MessageParts";
import { CHAT_API, CHAT_MESSAGES_API } from "./constants";

// ── Parts options cache ───────────────────────────────────────────────────────
let _partsCache = null;
let _partsFetch = null;
function fetchPartsOnce() {
  if (_partsCache) return Promise.resolve(_partsCache);
  if (_partsFetch) return _partsFetch;
  _partsFetch = axios.post(
    `${API_DOMAIN}/whatsapp/get_parts_info`, {},
    { headers: { accessToken: localStorage.getItem("accessToken") } }
  ).then(res => {
    const list = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    _partsCache = list.map(p =>
      typeof p === "string" ? { value: p, label: p } : { value: p.value ?? p.name, label: p.label ?? p.name ?? p.value }
    );
    _partsFetch = null;
    return _partsCache;
  }).catch(() => { _partsFetch = null; return []; });
  return _partsFetch;
}

// ── Thumbnail used in gallery picker ─────────────────────────────────────────
function GalleryPickerThumb({ item, selected, selectionIndex, onToggle, disabled }) {
  const [url, setUrl] = useState(
    item.source === "upload" || item.source === "existing" ? item.previewUrl : null
  );
  useEffect(() => {
    if (item.source === "upload" || item.source === "existing") return;
    if (!item?.data) return;
    _resolveMediaUrl(item.data).then(setUrl).catch(() => {});
  }, [item?.data, item?.source]);

  return (
    <div
      onClick={() => (!disabled || selected) && onToggle()}
      style={{
        position: "relative", aspectRatio: "1", borderRadius: 6, overflow: "hidden",
        cursor: disabled && !selected ? "not-allowed" : "pointer",
        border: selected ? "3px solid #1677ff" : "2px solid transparent",
        background: "#f0f0f0", flexShrink: 0,
        opacity: disabled && !selected ? 0.45 : 1,
        transition: "border-color 0.15s",
      }}
    >
      {url
        ? <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><Spin size="small" /></div>
      }
      {selected && (
        <div style={{
          position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%",
          background: "#1677ff", color: "#fff", fontSize: 11, fontWeight: 700,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
        }}>
          {selectionIndex}
        </div>
      )}
      <div style={{
        position: "absolute", bottom: 3, left: 3,
        background: "rgba(0,0,0,0.55)", borderRadius: 4,
        padding: "1px 4px", display: "flex", alignItems: "center",
      }}>
        {item.source === "chat"
          ? <WechatOutlined style={{ fontSize: 10, color: "#fff" }} />
          : <UploadOutlined style={{ fontSize: 10, color: "#fff" }} />
        }
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const BLANK_FORM = { vehicle_id: null, urgency: 2, assigned_to: null, remarks: "", parts: [], images: [] };

export function EditDraftDrawer({ queryPk, open, onClose, onSaved }) {
  const { authState } = useContext(AuthContext);
  const fileInputRef = useRef(null);

  const [taskLoading, setTaskLoading] = useState(false);
  const [task, setTask] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);

  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [agentOptions, setAgentOptions] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [partsOptions, setPartsOptions] = useState([]);
  const [partsLoading, setPartsLoading] = useState(false);
  const [activePartSearch, setActivePartSearch] = useState("");
  const [saving, setSaving] = useState(false);

  // Gallery picker modal state
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryTab, setGalleryTab] = useState("chat");
  const [chatImgs, setChatImgs] = useState([]);
  const [chatImgsLoading, setChatImgsLoading] = useState(false);
  const [chatImgsLoaded, setChatImgsLoaded] = useState(false);

  const headers = { accessToken: localStorage.getItem("accessToken") };

  useEffect(() => {
    if (!open || !queryPk) return;
    setTaskLoading(true);
    axios.get(`${API_DOMAIN}/query_workflow/task/get?query_pk=${queryPk}`, { headers })
      .then(res => {
        console.debug("[EditDraft] task/get response:", res.data);
        if (res.data.success) {
          const t = res.data.data?.task ?? null;
          const rawParts = res.data.data?.parts ?? t?.query_parts ?? [];
          setTask(t);
          if (t) {
            setForm({
              vehicle_id: t.vehicle_id ?? t.vehicle?.id ?? null,
              urgency: t.urgency ?? 2,
              assigned_to: t.assigned_user?.id ?? t.assigned_to ?? authState.id,
              remarks: t.remarks ?? "",
              parts: rawParts.map(p => ({
                part_name: p.part_name || "",
                quantity: p.quantity || 1,
                side: p.side ?? null,
                condition_preference: p.condition_preference ?? null,
                part_number: p.part_number ?? "",
              })),
              images: (Array.isArray(t.images) ? t.images : []).map((url, i) => ({
                id: `existing-${i}`,
                source: "existing",
                url,
                previewUrl: url.startsWith("http") ? url : `${API_DOMAIN}${url}`,
              })),
            });
            if (t.customer_id) {
              setVehiclesLoading(true);
              axios.get(`${API_DOMAIN}/customer_vehicles/get_by_customer?customer_id=${t.customer_id}`, { headers })
                .then(r => setVehicles(Array.isArray(r.data?.data) ? r.data.data : []))
                .catch(() => setVehicles([]))
                .finally(() => setVehiclesLoading(false));
            }
          }
        }
      })
      .catch(() => {})
      .finally(() => setTaskLoading(false));

    if (agentOptions.length === 0) {
      setAgentsLoading(true);
      _fetchSRMUsers().then(list => { setAgentOptions(list); setAgentsLoading(false); });
    }
    if (partsOptions.length === 0) {
      setPartsLoading(true);
      fetchPartsOnce().then(list => { setPartsOptions(list); setPartsLoading(false); });
    }
  }, [open, queryPk]); // eslint-disable-line

  const reset = () => {
    setTask(null);
    setForm(BLANK_FORM);
    setVehicles([]);
    setChatImgs([]);
    setChatImgsLoaded(false);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setPart = (idx, patch) => setForm(f => {
    const parts = [...f.parts];
    parts[idx] = { ...parts[idx], ...patch };
    return { ...f, parts };
  });

  // ── Load chat images on demand ──────────────────────────────────────────────
  async function loadChatImages() {
    const phone = task?.customer?.customer_mobile || task?.customer_mobile;
    if (!phone) { message.warning("No phone number found for this customer"); return; }
    setChatImgsLoading(true);
    try {
      // Step 1: resolve conversationId from phone via fetch_messages
      const searchRes = await axios.post(CHAT_API, { channelIds: "", search_filter: 0, search: phone, userId: authState.id }, { headers });
      const d = searchRes.data;
      // Defensive: some fields may be false/null rather than undefined, so check Array.isArray explicitly
      const chats = Array.isArray(d) ? d
        : Array.isArray(d?.data) ? d.data
        : Array.isArray(d?.final_data) ? d.final_data
        : Array.isArray(d?.data?.data) ? d.data.data
        : Array.isArray(d?.data?.final_data) ? d.data.final_data
        : [];
      console.debug("[EditDraft] chat search result:", { phone, rawData: d, chatsCount: chats.length, first: chats[0] });

      if (!chats.length) { message.info("No WhatsApp chat found for this customer"); return; }

      const phoneDigits = phone.replace(/\D/g, "");
      const matched = chats.find(c =>
        (c.phone || "").replace(/\D/g, "").endsWith(phoneDigits) ||
        (c.whatsappId || "").replace(/\D/g, "").endsWith(phoneDigits)
      ) ?? chats[0];

      const conversationId = matched?.conversationId ?? matched?.ticket?.conversationId ?? matched?.id;
      console.debug("[EditDraft] matched chat:", { conversationId, matched });

      if (!conversationId) { message.info("Could not resolve WhatsApp conversation ID"); return; }

      // Step 2: fetch messages and filter images
      const msgRes = await axios.post(
        CHAT_MESSAGES_API,
        { chatId: conversationId, lastMessageId: null, userId: authState.id },
        { headers }
      );
      console.debug("[EditDraft] messages response:", msgRes.data);

      const rawMsgs = msgRes.data?.data;
      const msgs = Array.isArray(rawMsgs) ? rawMsgs : (rawMsgs?.data ?? []);
      const imgs = msgs
        .filter(m => {
          const t = typeof m.type === "string" ? m.type.toLowerCase() : "";
          const mt = typeof m.messageType === "string" ? m.messageType.toLowerCase() : "";
          return (t === "image" || mt === "image") && m.data;
        })
        .map(m => ({ ...m, id: m.id || m.localMessageId || m.data }));
      console.debug("[EditDraft] images found:", imgs.length);

      setChatImgs(imgs);
      setChatImgsLoaded(true);
    } catch (err) {
      console.error("[EditDraft] loadChatImages error:", err);
      message.error(`Failed to load chat images: ${err?.response?.data?.message || err.message || "unknown"}`);
    } finally {
      setChatImgsLoading(false);
    }
  }

  // ── Upload from device ──────────────────────────────────────────────────────
  function addUploadFiles(files) {
    const slots = 10 - form.images.length;
    Array.from(files).slice(0, slots).forEach(file => {
      const reader = new FileReader();
      reader.onload = e => {
        setForm(f => ({
          ...f,
          images: [...f.images, {
            id: `upload-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            source: "upload",
            file,
            previewUrl: e.target.result,
          }],
        }));
      };
      reader.readAsDataURL(file);
    });
  }

  // ── Parts ───────────────────────────────────────────────────────────────────
  async function handleAddNewPart(value, idx) {
    try {
      await axios.post(`${API_DOMAIN}/whatsapp/add_new_part_info`, { value }, { headers });
      setPartsOptions(prev => [...prev, { value, label: value }]);
    } catch { message.error("Failed to save new part name"); return; }
    setPart(idx, { part_name: value });
    setActivePartSearch("");
  }

  // ── Save ────────────────────────────────────────────────────────────────────
  async function handleSave(andSend = false) {
    setSaving(true);
    try {
      const uploadedUrls = await Promise.all(form.images.map(async img => {
        if (img.source === "existing") return img.url;
        const fd = new FormData();
        if (img.source === "upload") {
          fd.append("file", img.file);
        } else {
          // chat image: download via proxy then re-upload
          const blobRes = await axios.post(
            `${API_DOMAIN}/wati_messages/download_wati_media_file`,
            { file_name: img.data },
            { headers, responseType: "blob" }
          );
          const blob = blobRes.data;
          const ext = (blob.type || "image/jpeg").split("/")[1] || "jpg";
          fd.append("file", new File([blob], `chat_image_${Date.now()}.${ext}`, { type: blob.type }));
        }
        const res = await axios.post(`${API_DOMAIN}/whatsapp/image_upload`, fd, { headers });
        return res.data;
      }));

      const validParts = form.parts
        .filter(p => p.part_name?.trim())
        .map(p => ({
          part_name: p.part_name.trim(),
          quantity: p.quantity || 1,
          ...(p.side && { side: p.side }),
          ...(p.condition_preference && { condition_preference: p.condition_preference }),
          ...(p.part_number?.trim() && { part_number: p.part_number.trim() }),
        }));

      const res = await axios.post(`${API_DOMAIN}/queryset_v2/update_draft`, {
        task_id: queryPk,
        ...(form.vehicle_id != null && { vehicle_id: form.vehicle_id }),
        urgency: form.urgency,
        assigned_to: form.assigned_to,
        ...(form.remarks?.trim() && { remarks: form.remarks.trim() }),
        parts: validParts,
        images: uploadedUrls,
      }, { headers });

      if (!res.data.success) throw new Error(res.data.message);

      const updated = res.data.data;
      const nowCanSend =
        !!(updated?.vehicle || updated?.vehicle_id) &&
        (updated?.query_parts?.length > 0) &&
        (updated?.images?.length > 0);

      message.success("Draft updated");
      reset();
      onSaved?.(updated, andSend && nowCanSend);
      onClose();
    } catch (e) {
      message.error(e.message || "Failed to update draft");
    } finally {
      setSaving(false);
    }
  }

  // ── Derived ─────────────────────────────────────────────────────────────────
  const vehicleOptions = vehicles.map(v => {
    const brand = v.brand_name || v.brand_label || v.brand || "";
    const model = v.model_name || v.model_label || v.model || "";
    const year = v.year ? `(${v.year})` : "";
    const spec = v.country_spec ? `(${v.country_spec.toUpperCase()})` : "";
    return { value: v.id, label: [brand, model, year, spec].filter(Boolean).join(" ") };
  });

  const hasVehicle = form.vehicle_id != null;
  const hasParts = form.parts.some(p => p.part_name?.trim());
  const hasImages = form.images.length > 0;
  const canSend = hasVehicle && hasParts && hasImages;
  const missing = [!hasVehicle && "vehicle", !hasParts && "parts", !hasImages && "images"].filter(Boolean);

  const toggleChatImage = (img) => {
    setForm(f => {
      const enriched = { ...img, id: img.id, source: "chat" };
      const already = f.images.findIndex(x => x.id === img.id);
      if (already >= 0) return { ...f, images: f.images.filter(x => x.id !== img.id) };
      if (f.images.length >= 10) return f;
      return { ...f, images: [...f.images, enriched] };
    });
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={handleClose}
        title={task ? `Edit Draft #${task.query_id}` : "Edit Draft"}
        width={480}
        styles={{
          body: { padding: "16px 20px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 16 },
          footer: { padding: "12px 20px" },
        }}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={handleClose} disabled={saving}>Cancel</Button>
            <Button loading={saving} onClick={() => handleSave(false)}>Save Draft</Button>
            <Tooltip title={!canSend ? `Missing: ${missing.join(", ")}` : ""}>
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={saving}
                disabled={!canSend}
                onClick={() => handleSave(true)}
              >
                Save & Send
              </Button>
            </Tooltip>
          </div>
        }
      >
        {taskLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 48 }}><Spin /></div>
        ) : (
          <>
            {/* Vehicle */}
            <div>
              <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Vehicle</div>
              <Select
                style={{ width: "100%" }}
                placeholder={vehiclesLoading ? "Loading vehicles…" : "Select vehicle"}
                loading={vehiclesLoading}
                allowClear
                value={form.vehicle_id ?? null}
                onChange={val => setField("vehicle_id", val ?? null)}
                options={vehicleOptions}
              />
            </div>

            {/* Urgency + Assigned To */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Urgency</div>
                <Select
                  style={{ width: "100%" }}
                  value={form.urgency}
                  onChange={val => setField("urgency", val)}
                  options={Object.entries(URGENCY_CFG).map(([k, v]) => ({ value: Number(k), label: v.label }))}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Assign To</div>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select agent"
                  loading={agentsLoading}
                  value={form.assigned_to ?? null}
                  onChange={val => setField("assigned_to", val ?? authState.id)}
                  options={agentOptions.map(a => ({ value: a.id, label: a.fullName }))}
                  optionRender={option => {
                    const name = String(option.label ?? "");
                    return (
                      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <Avatar size={14} style={{ backgroundColor: getAgentColor(name), fontSize: 8, flexShrink: 0 }}>{agentInitials(name)}</Avatar>
                        {name}
                      </span>
                    );
                  }}
                />
              </div>
            </div>

            {/* Remarks */}
            <div>
              <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Remarks</div>
              <Input.TextArea rows={2} placeholder="Optional notes…" value={form.remarks} onChange={e => setField("remarks", e.target.value)} />
            </div>

            {/* Parts */}
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>Parts</div>
                <Button
                  size="small" icon={<PlusOutlined />}
                  onClick={() => setField("parts", [...form.parts, { part_name: "", quantity: 1, side: null, condition_preference: "used", part_number: "" }])}
                >
                  Add Part
                </Button>
              </div>
              {form.parts.length === 0 ? (
                <div style={{ fontSize: 12, color: "#bfbfbf", textAlign: "center", padding: "6px 0" }}>No parts added yet</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {form.parts.map((part, idx) => (
                    <div key={idx} style={{ border: "1px solid #d0e4ff", borderRadius: 6, padding: "8px 10px", background: "#fff", display: "flex", flexDirection: "column", gap: 6 }}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <Select
                          showSearch size="small" style={{ flex: 1 }}
                          placeholder="Part name *"
                          loading={partsLoading}
                          value={part.part_name || undefined}
                          onSearch={setActivePartSearch}
                          onChange={(val, opt) => { setActivePartSearch(""); setPart(idx, { part_name: String(opt?.label ?? val ?? "") }); }}
                          filterOption={(input, opt) => String(opt.label ?? "").toLowerCase().includes(input.toLowerCase())}
                          options={partsOptions}
                          dropdownRender={menu => (
                            <>
                              {menu}
                              {activePartSearch && !partsOptions.some(o => String(o.label ?? "").toLowerCase() === activePartSearch.toLowerCase()) && (
                                <div
                                  style={{ padding: "6px 12px", cursor: "pointer", color: "#1677ff", fontSize: 12, borderTop: "1px solid #f0f0f0" }}
                                  onMouseDown={e => { e.preventDefault(); handleAddNewPart(activePartSearch, idx); }}
                                >
                                  + Add "{activePartSearch}"
                                </div>
                              )}
                            </>
                          )}
                        />
                        <Input
                          type="number" min={1} value={part.quantity}
                          onChange={e => setPart(idx, { quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                          style={{ width: 56 }} size="small"
                          prefix={<span style={{ fontSize: 10, color: "#8c8c8c" }}>×</span>}
                        />
                        <Button type="text" danger size="small" icon={<DeleteOutlined />}
                          onClick={() => setField("parts", form.parts.filter((_, i) => i !== idx))} />
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                        <Select size="small" placeholder="Side" allowClear value={part.side ?? null}
                          onChange={val => setPart(idx, { side: val ?? null })}
                          options={[
                            { value: "left", label: "Left" }, { value: "right", label: "Right" },
                            { value: "both", label: "Both" }, { value: "front", label: "Front" },
                            { value: "rear", label: "Rear" }, { value: "front_left", label: "Front Left" },
                            { value: "front_right", label: "Front Right" }, { value: "rear_left", label: "Rear Left" },
                            { value: "rear_right", label: "Rear Right" },
                          ]}
                        />
                        <Select size="small" placeholder="Condition" allowClear value={part.condition_preference ?? null}
                          onChange={val => setPart(idx, { condition_preference: val ?? null })}
                          options={[{ value: "new", label: "New" }, { value: "used", label: "Used" }, { value: "refurbished", label: "Refurbished" }]}
                        />
                        <Input size="small" placeholder="Part #" value={part.part_number}
                          onChange={e => setPart(idx, { part_number: e.target.value })} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Images */}
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                  Images{form.images.length > 0 && <span style={{ marginLeft: 6, color: "#1677ff", fontWeight: 600 }}>{form.images.length} / 10</span>}
                </div>
                {form.images.length < 10 && (
                  <Button size="small" onClick={() => { setGalleryTab("chat"); setGalleryOpen(true); }}>
                    Add Images
                  </Button>
                )}
              </div>
              {form.images.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6 }}>
                  {form.images.map((img, i) => (
                    <GalleryPickerThumb
                      key={img.id}
                      item={img}
                      selected
                      selectionIndex={i + 1}
                      onToggle={() => setField("images", form.images.filter(x => x.id !== img.id))}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </Drawer>

      {/* Image picker modal */}
      <Modal
        open={galleryOpen}
        onCancel={() => setGalleryOpen(false)}
        title="Add Images"
        width={500}
        styles={{ body: { padding: 0 } }}
        footer={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 12, color: "#8c8c8c" }}>{form.images.length} / 10 selected</span>
            <Button type="primary" onClick={() => setGalleryOpen(false)}>Done</Button>
          </div>
        }
        destroyOnClose={false}
      >
        <Tabs
          activeKey={galleryTab}
          onChange={setGalleryTab}
          size="small"
          tabBarStyle={{ margin: 0, paddingLeft: 16, paddingRight: 16 }}
          items={[
            {
              key: "chat",
              label: `From Chat${chatImgs.length ? ` (${chatImgs.length})` : ""}`,
              children: (
                <div style={{ padding: "12px 16px", minHeight: 160, maxHeight: 380, overflowY: "auto" }}>
                  {!chatImgsLoaded ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: "32px 0" }}>
                      <WechatOutlined style={{ fontSize: 32, color: "#bfbfbf" }} />
                      <div style={{ fontSize: 13, color: "#8c8c8c" }}>Load images from the customer's WhatsApp chat</div>
                      <Button
                        type="primary"
                        loading={chatImgsLoading}
                        onClick={loadChatImages}
                      >
                        Load Chat Images
                      </Button>
                    </div>
                  ) : chatImgs.length === 0 ? (
                    <Empty description="No images found in this chat" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: "24px 0" }} />
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
                      {chatImgs.map(img => {
                        const isSelected = form.images.some(x => x.id === img.id);
                        const selectionIndex = form.images.findIndex(x => x.id === img.id) + 1;
                        const atLimit = form.images.length >= 10;
                        return (
                          <GalleryPickerThumb
                            key={img.id}
                            item={{ ...img, source: "chat" }}
                            selected={isSelected}
                            selectionIndex={isSelected ? selectionIndex : 0}
                            disabled={atLimit && !isSelected}
                            onToggle={() => toggleChatImage(img)}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: "upload",
              label: "Upload from Device",
              children: (
                <div style={{ padding: "12px 16px" }}>
                  <input
                    ref={fileInputRef}
                    type="file" multiple accept="image/*" style={{ display: "none" }}
                    disabled={form.images.length >= 10}
                    onChange={e => { addUploadFiles(e.target.files); e.target.value = ""; }}
                  />
                  <label
                    style={{
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      border: "1px dashed #d9d9d9", borderRadius: 8, padding: "24px 16px",
                      cursor: form.images.length >= 10 ? "not-allowed" : "pointer",
                      background: "#fafafa", marginBottom: 12,
                      opacity: form.images.length >= 10 ? 0.5 : 1,
                    }}
                    onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = "#1677ff"; }}
                    onDragLeave={e => { e.currentTarget.style.borderColor = "#d9d9d9"; }}
                    onDrop={e => {
                      e.preventDefault();
                      e.currentTarget.style.borderColor = "#d9d9d9";
                      if (form.images.length >= 10) return;
                      addUploadFiles(Array.from(e.dataTransfer.files).filter(f => f.type.startsWith("image/")));
                    }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <InboxOutlined style={{ fontSize: 32, color: "#8c8c8c", marginBottom: 8 }} />
                    <div style={{ fontSize: 13, color: "#595959", fontWeight: 500 }}>Click or drag images here</div>
                    <div style={{ fontSize: 11, color: "#bfbfbf", marginTop: 4 }}>
                      {form.images.length >= 10 ? "Limit reached (10)" : `Up to ${10 - form.images.length} more`}
                    </div>
                  </label>
                  {form.images.filter(i => i.source === "upload").length > 0 && (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
                      {form.images.filter(i => i.source === "upload").map(img => (
                        <GalleryPickerThumb
                          key={img.id}
                          item={img}
                          selected
                          selectionIndex={form.images.findIndex(x => x.id === img.id) + 1}
                          onToggle={() => setField("images", form.images.filter(x => x.id !== img.id))}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ),
            },
          ]}
        />
      </Modal>
    </>
  );
}
